/**
 * The full inventory (2026-09-02).
 *
 * User: "about inventory I've mentioned like full inventory button and full
 * inventory window where I can interact."
 *
 * The strip under the Talents button (`ui/loadout-panel.ts`) answers "what am
 * I walking in with" at a glance and is deliberately not operable. This is the
 * other half: everything you own in one frame, with the things you can
 * actually do to it.
 *
 * ## Why it is a paper doll beside a container, and not a third grid
 *
 * The pieces already existed but in three places — gear in the hero card, loot
 * in the Stash panel, charges in a Shop tab — so "how much can I still carry"
 * meant opening three screens and doing the arithmetic. Here the left column
 * is the delver (worn, carried, weight) and the right is the stash, which is
 * the shape every inventory screen in the genre has for the same reason: the
 * two halves are read against each other.
 *
 * ## What you can do in it
 *
 * Selecting a stash slot opens its detail with a Sell button — the one
 * destructive action here, so it is never on the tile itself where a
 * mis-tap costs you an item. Capacity gets its own bar and its own Expand
 * button, because a full stash silently costs you loot at the end of a run
 * and that is the moment this screen exists to prevent.
 *
 * Empty slots are drawn rather than omitted. A stash with four things in it
 * and twenty-one gaps is a different feeling from a list of four rows, and
 * the gaps are the thing you are buying when you press Expand.
 *
 * Pure string builders, same as the rest of `ui/`.
 */

import type { StashItemRow } from '../net/hub-api';
import type { LoadoutRow } from '../net/loadout-api';
import { ITEM_DEFS } from '../config/item-defs';
import { itemIconSrc, itemTitle, itemSubtitle, rarityFrameClass, rarityTextClass, esc } from './item-icon';
import { amountMarkup } from './currency';
import { paperdollMarkup } from './paperdoll';
import type { EquippedArms, EquipmentSlot, EquipmentCatalogRow } from '../net/equipment-api';
import { pickerMarkup, gearPickerMarkup } from './paperdoll';
import type { GearCatalogRow, GearBonuses } from '../net/gear-api';
import type { GearSlot } from '../config/gear-defs';
import { GEAR_SLOT_LABEL } from '../config/gear-defs';
import type { CharacterClassId } from '../config/character-roster';
import type { CosmeticItemRow } from '../net/cosmetics-api';
import { cosmeticIconSrc, ICON_FALLBACK_ATTR } from './item-icon';
import { cosmeticLabel } from './shop';
import { t, tc, formatNumber } from '../i18n/i18n';

/** How many slots the grid draws when capacity is unknown — never zero, which would read as a broken panel. */
const FALLBACK_CAPACITY = 25;

export interface InventoryView {
  stash: readonly StashItemRow[];
  capacity: number;
  loadout: LoadoutRow | null;
  /** The stash row currently open in the detail pane, if any. */
  selectedId: string | null;
  characterName: string;
  characterSub: string;
  /** Wax the vendor would pay, keyed by stash row id — priced by the caller, which owns the pricing sim. */
  sellPrices: Readonly<Record<string, number>>;
  /** Already-built markup for the Expand button's label; it carries a Wax glyph. */
  expandLabel: string;
  expandDisabled: boolean;
  /**
   * design/gdd/cosmetics-store.md — what this delver owns to look like, and
   * what every delver on the account can play (2026-09-07).
   *
   * User: *"Skins and emojis should be equiped here. Not in shop for sure."*
   *
   * `skins` is filtered to this class by the caller, because a skin belongs to
   * one class and a picker offering a Bonepicker to a druid is a row that
   * cannot be pressed. `emotes` is not filtered by anything: an emote is an
   * account unlock playable by every delver, which is exactly the fact this
   * section exists to make visible.
   */
  skins: readonly CosmeticItemRow[];
  emotes: readonly CosmeticItemRow[];
  /**
   * defId -> display name, from the catalogue.
   *
   * An owned `CosmeticItemRow` carries no name — only the def id — so without
   * this the wardrobe would call the Gilded Warden "Knight Gilded", which is
   * the id with its underscores taken out. `cosmeticLabel` already falls back
   * to exactly that, so a missing entry reads oddly rather than breaking, and
   * a client that outlives its catalogue fetch still renders.
   */
  cosmeticNames: Readonly<Record<string, string>>;
  /**
   * The delver this window is showing, so the wardrobe can tell "worn here"
   * from "worn by another delver of the same class".
   *
   * The second is not a locked row — picking it moves the skin, because a
   * skin is one thing and lives in one place. It is only labelled differently,
   * so choosing it is a decision rather than a surprise.
   */
  wornByCharacterId: string;
  /** Open picker, or null. Mutually exclusive with the arms and gear pickers — they share a column. */
  skinPickerOpen: boolean;
  /** design/gdd/equipment.md — what is in the two live paperdoll slots. */
  classId: CharacterClassId;
  arms: EquippedArms | null;
  /** The arms slot whose picker is open, if any. */
  pickerSlot: EquipmentSlot | null;
  equipmentCatalog: readonly EquipmentCatalogRow[];
  /** equipment.md's worn-gear amendment — the four slots the ladder filled. */
  worn: Partial<Record<GearSlot, GearCatalogRow | null>>;
  gearCatalog: readonly GearCatalogRow[];
  gearBonuses: GearBonuses;
  /** The gear slot whose picker is open, if any. Mutually exclusive with `pickerSlot`. */
  gearPickerSlot: GearSlot | null;
}

/**
 * Identical items, drawn once (2026-09-05).
 *
 * User: *"add stacking for identical items."* Three Gilded Reliquaries were
 * three tiles that looked, and were, exactly the same.
 *
 * This is a **display** grouping and nothing more. `StashItem` is one row per
 * object — each carries its own `acquiredRunId` and its own insurance state —
 * so a stack is a way of drawing rows, not a way of storing them, and every
 * action still names the individual row it acts on.
 *
 * Identity is the item id *and* the rarity. Weight and value come from the
 * def, so two rows of the same def agree about those; rarity does not change
 * the price but it does change the frame, the name's colour and the subtitle,
 * and merging a RARE into a COMMON would be drawing a lie.
 *
 * Order is first-appearance, so a stack sits where its earliest member sat and
 * the grid does not reshuffle when one is sold.
 */
export interface StashStack {
  /** The row the tile stands for — icon, rarity and detail all come from this one. */
  item: StashItemRow;
  /** Every row in it, in the order they were found; selling takes the oldest first. */
  ids: readonly string[];
  count: number;
}

export function stackStash(stash: readonly StashItemRow[]): StashStack[] {
  const byKey = new Map<string, StashStack & { ids: string[] }>();
  const out: (StashStack & { ids: string[] })[] = [];
  for (const item of stash) {
    // A NUL between the parts so an id that happens to contain the separator
    // cannot collide with a different (id, rarity) pair.
    const key = `${item.itemDefId}\u0000${item.rarity}`;
    const found = byKey.get(key);
    if (found) {
      found.ids.push(item.id);
      found.count += 1;
      continue;
    }
    const stack = { item, ids: [item.id], count: 1 };
    byKey.set(key, stack);
    out.push(stack);
  }
  return out;
}

/**
 * One stash slot.
 *
 * A button even when empty: an empty slot is a legitimate thing to tap on a
 * phone (it is how you find out the grid is interactive at all), and it
 * clears the selection, which is the only way back out of the detail pane
 * without picking something else.
 */
export function slotMarkup(stack: StashStack | null, selected: boolean): string {
  // RARE already wears a gold frame, so a gold ring alone was not enough to
  // tell "this is the one I tapped" from "this one is rare". The wash is what
  // actually separates them.
  const ring = selected
    ? 'border-gold-bright ring-2 ring-gold-bright/55 bg-gold-bright/10 shadow-[0_0_12px_rgba(243,208,137,0.28)]'
    // An empty slot draws as a socket (`frame-empty`), so it needs no border
    // class of its own — a dashed outline on top of it read as a form field.
    : stack ? rarityFrameClass(stack.item.rarity) : '';
  if (!stack) {
    return `<button data-inv-slot="" aria-label="${esc(t('Empty slot'))}"
      class="frame-empty relative aspect-square min-h-11 ${ring}"></button>`;
  }
  const { item, count } = stack;
  const title = itemTitle(item.itemDefId);
  const label = count > 1 ? t('{name} \u00d7{count}', { name: title, count }) : title;
  // The same badge the satchel wears for charges, for the same reason: a
  // number in the corner of a tile means "how many of these", and this screen
  // should not invent a second way of saying it.
  const badge = count > 1
    ? `<span class="absolute bottom-0.5 right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full
                    border border-[#2b2219] bg-[#d8ab5c] text-[#241a10] text-[10px] font-bold tabular-nums
                    pointer-events-none">${count}</span>`
    : '';
  return `
    <button data-inv-slot="${esc(item.id)}" aria-pressed="${selected}" title="${esc(label)}" aria-label="${esc(label)}"
      class="frame-inset relative aspect-square min-h-11 overflow-hidden ${ring}">
      <img ${ICON_FALLBACK_ATTR} src="${esc(itemIconSrc(item.itemDefId))}" alt="${esc(title)}" loading="lazy"
           class="absolute inset-0 w-full h-full object-contain p-1" />
      ${badge}
    </button>
  `;
}

/**
 * The grid: every owned item, then every slot they have not filled.
 *
 * Capacity can legitimately be smaller than what is in the stash — an item
 * arrives from a run before the wallet read lands, and expanding is what
 * fixes it — so the grid draws at least as many cells as there are items
 * rather than hiding the overflow.
 */
export function stashGridMarkup(view: InventoryView): string {
  const capacity = view.capacity > 0 ? view.capacity : FALLBACK_CAPACITY;
  const stacks = stackStash(view.stash);
  // The invariant stacking must not break: **an empty socket is a free slot**.
  // Capacity counts items, not stacks — three reliquaries still cost three of
  // your twenty-five — so the gaps are drawn against the item count, and the
  // grid keeps meaning what Expand is selling. A stash that overflows capacity
  // (an item lands from a run before the wallet read does) simply has no gaps.
  const free = Math.max(0, capacity - view.stash.length);
  const slots: string[] = [];
  for (const stack of stacks) {
    slots.push(slotMarkup(stack, stack.ids.includes(view.selectedId ?? '')));
  }
  for (let i = 0; i < free; i++) slots.push(slotMarkup(null, false));
  // Auto-fill from a 56px floor rather than a fixed column count
  // (2026-09-06). A fixed eight columns still drew a 76px tile beside the
  // doll's 48px slot on a wide panel, and the same window calling one object
  // two sizes is the thing this grid keeps getting wrong. Auto-fill also means
  // a wider window shows *more stash* instead of bigger sockets, which is what
  // a stash is for. The floor is above the 44px tap target with room for the
  // frame.
  return `<div class="grid grid-cols-[repeat(auto-fill,minmax(56px,1fr))] gap-1.5">${slots.join('')}</div>`;
}

/**
 * The detail pane.
 *
 * Holds its own height whether or not something is selected: a pane that
 * appears and disappears reflows the grid above it every time you tap a
 * different item, which on a phone means the thing you were aiming at moves.
 */
export function detailMarkup(view: InventoryView): string {
  const stack = view.selectedId
    ? stackStash(view.stash).find((s) => s.ids.includes(view.selectedId!)) ?? null
    : null;
  const item = stack?.item ?? null;
  if (!item || !stack) {
    return `
      <div class="frame-inset min-h-[92px] flex items-center justify-center px-3 text-center">
        <p class="text-[12px] text-text-dim">${esc(t('Select an item to see what it is worth, and to sell it.'))}</p>
      </div>
    `;
  }
  const title = itemTitle(item.itemDefId);
  const price = view.sellPrices[item.id] ?? 0;
  const def = ITEM_DEFS[item.itemDefId];
  const value = def ? t('Worth {n} new', { n: def.baseValue }) : '';
  // Stacks below `sm` (2026-09-05): at 320px the icon and the Sell button left
  // the text column about ninety pixels wide, and the pane read "Gilded…",
  // "3.5kg · …", "A curio —". Every fact it exists to state was truncated away
  // on the narrowest screen the game supports.
  return `
    <div class="frame-inset min-h-[92px] flex flex-col sm:flex-row sm:items-center gap-3 p-2.5">
      <div class="flex items-center gap-3 min-w-0 flex-1">
      <span class="block w-14 shrink-0 aspect-square rounded-md border ${rarityFrameClass(item.rarity)} overflow-hidden relative">
        <img ${ICON_FALLBACK_ATTR} src="${esc(itemIconSrc(item.itemDefId))}" alt="" class="absolute inset-0 w-full h-full object-contain p-1" />
      </span>
      <div class="min-w-0 flex-1">
        <p class="text-[14px] truncate ${rarityTextClass(item.rarity)}">${esc(title)}${
  stack.count > 1 ? `<span class="text-text-dim"> &times;${stack.count}</span>` : ''
}</p>
        <p class="text-[11px] text-text-dim truncate">${esc(itemSubtitle(item.itemDefId, item.rarity))}</p>
        <!--
          What it is, said outright (2026-09-04). User, pointing at a Gilded
          Reliquary in this exact pane: "I also do not understand what this
          item is for, or where and how to use it." The pane showed a weight,
          a rarity, a resale value and a Sell button — every fact about the
          object except the one that answers the question. A curio has one
          verb, and the pane now says so before it says anything else.
        -->
        <p class="text-[11px] text-text-dim/85 leading-snug">${esc(t('A curio — nothing to equip or use. Sell it, or deliver it to a commission.'))}</p>
        ${value ? `<p class="text-[11px] text-text-dim truncate">${esc(value)}</p>` : ''}
        <!--
          Sits under the name rather than beside the Sell button: Sell is the
          one irreversible thing on this screen, and a second control next to
          it that a mis-tap could hit instead is exactly what putting Sell off
          the tile was meant to avoid.
        -->
        <div class="flex items-center gap-4 flex-wrap">
          <button data-detail-curio="${esc(item.itemDefId)}"
            class="btn btn-link px-0 min-h-11 text-[11px] text-text-dim underline-offset-2 hover:underline">
            ${esc(t('What is this?'))}
          </button>
          <!--
            Sell-all lives here, beside "What is this?", and not beside Sell.
            Selling one and selling six are the same gesture two pixels apart
            otherwise, and Sell is already the one irreversible thing on this
            screen — the reason it was moved off the tile in the first place.
            It confirms; the single Sell does not, because the size of the
            mistake is what makes a gate worth its friction.
          -->
          ${stack.count > 1 ? `<button data-inv-sell-all="${esc(stack.ids.join(','))}"
            class="btn btn-link px-0 min-h-11 text-[11px] text-text-dim underline-offset-2 hover:underline">
            ${esc(t('Sell all {n}', { n: stack.count }))}<span class="ml-1">${amountMarkup('wax', price * stack.count, { hideLabel: true, tone: 'text-text-dim' })}</span>
          </button>` : ''}
        </div>
      </div>
      </div>
      <button data-inv-sell="${esc(item.id)}"
        class="btn btn-stone shrink-0 w-full sm:w-auto">
        <span class="inline-flex items-center gap-1">${esc(t('Sell'))}${amountMarkup('wax', price, { hideLabel: true })}</span>
      </button>
    </div>
  `;
}

/**
 * The capacity meter.
 *
 * Amber near the cap and red at it, matching the Stash panel's own bar —
 * being full is the state that silently costs you loot at the end of a run.
 */
export function capacityMarkup(used: number, capacity: number): string {
  const safeCapacity = Math.max(0, capacity);
  const safeUsed = Math.max(0, used);
  const pct = safeCapacity > 0 ? Math.min(100, (safeUsed / safeCapacity) * 100) : 0;
  const full = safeCapacity > 0 && safeUsed >= safeCapacity;
  const near = !full && pct >= 80;
  const tint = full ? 'bg-error' : near ? 'bg-warn' : 'bg-success';
  return `
    <div class="flex items-center gap-2">
      <div class="meter flex-1 h-2">
        <div class="meter-fill absolute inset-y-0 left-0 ${tint}" style="width:${pct}%"></div>
      </div>
      <span class="text-[12px] text-text-dim tabular-nums shrink-0">${safeUsed} / ${safeCapacity}</span>
    </div>
    ${full ? `<p class="text-[11px] text-error mt-1">${esc(t('Full — sell something before your next run.'))}</p>` : ''}
  `;
}

/** The whole window body. The overlay, title and close button are the hub's shared panel shell. */
export function inventoryMarkup(view: InventoryView): string {
  return `
    <div class="grid gap-4 lg:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
      <div class="flex flex-col gap-3 self-start">
        <div class="frame-inset p-3">
          <p class="font-heading text-[15px] truncate">${esc(view.characterName)}</p>
          <p class="eyebrow truncate">${esc(view.characterSub)}</p>
        </div>
        <div class="frame-inset p-3 flex flex-col gap-2.5">
          ${paperdollMarkup({
            classId: view.classId,
            characterName: view.characterName,
            arms: view.arms,
            loadout: view.loadout,
            worn: view.worn,
          })}
          <!--
            Inside the doll's own frame (2026-09-05), under a rule. It used to
            float below the panel as a loose sentence with nothing around it,
            which read as a caption for the whole window rather than as the
            last fact about the figure — and it is the figure's fact: it is
            what this loadout leaves you room to carry out.
          -->
          ${view.loadout ? carryLineMarkup(view.loadout) : ''}
        </div>
        ${lookMarkup(view)}
      </div>

      ${view.skinPickerOpen ? skinPaneMarkup(view)
    : view.gearPickerSlot ? gearPaneMarkup(view)
      : view.pickerSlot ? pickerPaneMarkup(view)
        : stashPaneMarkup(view)}
    </div>
  `;
}

/**
 * What this loadout leaves you room to carry out.
 *
 * Two numbers on one line rather than a sentence (2026-09-06). It read "22kg
 * free of 25 once you are holding all this.", which at 260px wrapped to three
 * lines and broke as "all / this." — a caption whose last line is one word is
 * a caption nobody finishes reading. The facts are the weight worn and the cap
 * it counts against; the spare is what you actually act on, so it leads.
 */
function carryLineMarkup(loadout: NonNullable<InventoryView['loadout']>): string {
  const round = (n: number): number => Math.round(n * 10) / 10;
  const spare = Math.max(0, round(loadout.capacityKg - loadout.startWeightKg));
  const worn = round(loadout.startWeightKg);
  // Amber when a run's worth of loot would not fit: 5kg is roughly two curios
  // and the smallest haul worth walking out with.
  const tight = spare < 5;
  return `
    <div class="border-t border-[#4a3c2d]/70 pt-2 flex items-baseline justify-between gap-2 text-[11px]">
      <span class="text-text-dim">${esc(t('Carrying {worn}kg of {cap}kg', { worn: formatNumber(worn), cap: formatNumber(loadout.capacityKg) }))}</span>
      <span class="${tight ? 'text-warn' : 'text-[#c2a06a]'} tabular-nums shrink-0">${esc(t('{n}kg spare', { n: formatNumber(spare) }))}</span>
    </div>
  `;
}

/** The picker, in the column the stash usually occupies — one thing at a time on a phone. */
function pickerPaneMarkup(view: InventoryView): string {
  const slot = view.pickerSlot!;
  const options = view.equipmentCatalog.filter((o) => o.slot === slot);
  const equippedId = slot === 'WEAPON' ? view.arms?.weapon.id ?? null : view.arms?.offhand?.id ?? null;
  return `
    <div class="flex flex-col gap-3 min-w-0">
      <div class="flex items-center justify-between gap-3">
        <p class="eyebrow">${esc(slot === 'WEAPON' ? t('Weapon') : t('Off-hand'))}</p>
        <button data-equip-cancel class="btn btn-quiet">${esc(t('Back to stash'))}</button>
      </div>
      ${pickerMarkup(slot, options, equippedId)}
    </div>
  `;
}

/**
 * The worn-gear picker, in the column the stash usually occupies.
 *
 * One pane at a time on a phone, exactly like the arms picker — and the two
 * are mutually exclusive because they compete for the same column.
 */
/**
 * How this delver looks, and what they can play (2026-09-07).
 *
 * User: *"Skins and emojis should be equiped here. Not in shop for sure."*
 *
 * Right, and it was the one thing you owned that had no home outside the till.
 * Arms are bought in the Shop and equipped on the paperdoll; gear is bought in
 * the Shop and equipped on the paperdoll; a skin was bought in the Shop and
 * equipped *in the Shop*, which made the Cosmetics tab the only shelf that was
 * also a wardrobe. This is the wardrobe.
 *
 * **Skins and emotes are drawn differently because they are different things,
 * and flattening them would be the lie.** A skin is equipped — one per class,
 * swappable, and the figure above changes when it does — so it gets a slot,
 * like every other equippable in this window. An emote is not equipped at all:
 * owning one puts it in the in-run wheel for every delver on the account, and
 * `handleEmote` checks ownership rather than any equipped set. Giving emotes a
 * slot would have meant inventing an equip step the game does not have, and a
 * control whose only possible state is "on" is a control that teaches a rule
 * that is not true.
 *
 * So they are shown as what they are: a row of what you can play, stated once.
 */
function lookMarkup(view: InventoryView): string {
  const equipped = view.skins.find((s) => s.equippedCharacterId === view.wornByCharacterId);
  const skinTile = equipped
    ? `<img ${ICON_FALLBACK_ATTR} src="${esc(cosmeticIconSrc(equipped.cosmeticDefId))}" alt=""
             class="absolute inset-0 w-full h-full object-contain p-1" />`
    : `<span class="absolute inset-0 grid place-items-center text-[11px] text-text-dim">${esc(t('Default'))}</span>`;

  const emoteTiles = view.emotes.length === 0
    ? `<p class="text-[11px] text-text-dim">${esc(t('None yet — the Shop sells them.'))}</p>`
    : `<div class="flex flex-wrap gap-1.5">${view.emotes.map((e) => `
        <span title="${esc(cosmeticLabel(e.cosmeticDefId, view.cosmeticNames[e.cosmeticDefId]))}"
              class="relative w-9 h-9 rounded border border-border bg-surface-2 overflow-hidden shrink-0">
          <img ${ICON_FALLBACK_ATTR} src="${esc(cosmeticIconSrc(e.cosmeticDefId))}" alt="${esc(cosmeticLabel(e.cosmeticDefId, view.cosmeticNames[e.cosmeticDefId]))}"
               class="absolute inset-0 w-full h-full object-contain p-1" />
        </span>`).join('')}</div>`;

  return `
    <div class="frame-inset p-3 flex flex-col gap-3">
      <div class="flex items-center gap-2.5">
        <button data-skin-open class="relative w-14 h-14 shrink-0 rounded-lg border-2 border-border
                       hover:border-gold/60 focus-visible:border-gold overflow-hidden bg-surface-2"
                aria-label="${esc(t('Change skin'))}">${skinTile}</button>
        <div class="min-w-0 flex-1">
          <p class="eyebrow">${esc(t('Skin'))}</p>
          <p class="text-[12px] text-text-dim truncate">${
  equipped ? esc(cosmeticLabel(equipped.cosmeticDefId, view.cosmeticNames[equipped.cosmeticDefId])) : esc(t('The delver as they come'))
}</p>
        </div>
        <button data-skin-open class="btn btn-stone shrink-0 px-3 text-[12px]">${esc(t('Change'))}</button>
      </div>
      <div class="border-t border-border/50 pt-2.5">
        <p class="eyebrow mb-1.5">${esc(t('Emotes'))}</p>
        ${emoteTiles}
        ${view.emotes.length > 0
    ? `<p class="text-[11px] text-text-dim mt-1.5">${esc(t('Carried by every delver you own. Press {key} in a run.', { key: 'B' }))}</p>`
    : ''}
      </div>
    </div>
  `;
}

/**
 * The skin picker, in the same column the gear and arms pickers use.
 *
 * "None" is a row rather than a separate Unequip button, for the reason the
 * gear picker gives about its own: taking something off is choosing to wear
 * nothing, and a list where that is one of the options needs no second control
 * to express it.
 */
function skinPaneMarkup(view: InventoryView): string {
  const rows = view.skins.map((s) => {
    const on = s.equippedCharacterId === view.wornByCharacterId;
    const elsewhere = !on && s.equippedCharacterId !== null;
    const cls = on
      ? 'w-full min-h-11 flex items-center gap-2.5 px-2 py-1.5 rounded border border-gold/70 bg-surface-2 text-left'
      : 'w-full min-h-11 flex items-center gap-2.5 px-2 py-1.5 rounded border border-border text-left hover:border-gold/40';
    return `
      <button data-skin-pick="${esc(s.cosmeticDefId)}" class="${cls}">
        <span class="relative w-9 h-9 shrink-0 rounded border border-border overflow-hidden bg-surface">
          <img ${ICON_FALLBACK_ATTR} src="${esc(cosmeticIconSrc(s.cosmeticDefId))}" alt="" class="absolute inset-0 w-full h-full object-contain p-1" />
        </span>
        <span class="min-w-0 flex-1 text-[13px] truncate">${esc(cosmeticLabel(s.cosmeticDefId, view.cosmeticNames[s.cosmeticDefId]))}</span>
        ${on ? `<span class="text-[11px] text-gold shrink-0">${esc(t('Worn'))}</span>`
    : elsewhere ? `<span class="text-[11px] text-text-dim shrink-0">${esc(t('On another delver'))}</span>` : ''}
      </button>`;
  }).join('');

  const none = `
    <button data-skin-pick="" class="w-full min-h-11 flex items-center gap-2.5 px-2 py-1.5 rounded
                   border border-border text-left hover:border-gold/40">
      <span class="w-9 h-9 shrink-0 rounded border border-border grid place-items-center text-[11px] text-text-dim">—</span>
      <span class="min-w-0 flex-1 text-[13px]">${esc(t('Default'))}</span>
      ${view.skins.some((s) => s.equippedCharacterId === view.wornByCharacterId)
    ? '' : `<span class="text-[11px] text-gold shrink-0">${esc(t('Worn'))}</span>`}
    </button>`;

  return `
    <div class="flex flex-col gap-3 min-w-0">
      <div class="flex items-center justify-between gap-3">
        <p class="eyebrow">${esc(t('Skin'))}</p>
        <button data-skin-cancel class="btn btn-quiet">${esc(t('Back to stash'))}</button>
      </div>
      ${view.skins.length === 0
    ? `<p class="text-[13px] text-text-dim">${esc(t('No skins for this class yet. The Shop sells them.'))}</p>`
    : `<div class="flex flex-col gap-1.5">${none}${rows}</div>`}
    </div>
  `;
}

function gearPaneMarkup(view: InventoryView): string {
  const slot = view.gearPickerSlot!;
  const options = view.gearCatalog.filter((o) => o.slot === slot);
  const equippedId = view.worn[slot]?.id ?? null;
  return `
    <div class="flex flex-col gap-3 min-w-0">
      <div class="flex items-center justify-between gap-3">
        <p class="eyebrow">${esc(tc('gear slot', GEAR_SLOT_LABEL[slot]))}</p>
        <button data-gear-cancel class="btn btn-quiet">${esc(t('Back to stash'))}</button>
      </div>
      ${gearPickerMarkup(options, equippedId, view.gearBonuses)}
    </div>
  `;
}

function stashPaneMarkup(view: InventoryView): string {
  return `
      <div class="flex flex-col gap-3 min-w-0">
        <div class="flex items-center gap-3 flex-wrap">
          <div class="flex-1 min-w-[160px]">
            <p class="eyebrow eyebrow-dim mb-1">${esc(t('Stash'))}</p>
            ${capacityMarkup(view.stash.length, view.capacity)}
          </div>
          <button data-inv-expand ${view.expandDisabled ? 'disabled' : ''}
            class="btn btn-stone shrink-0">${view.expandLabel}</button>
        </div>
        ${stashGridMarkup(view)}
        ${detailMarkup(view)}
      </div>
  `;
}
